import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageHeader } from '@carrent/ui'
import { orgApi } from '@carrent/api'
import { useAuthStore } from '@carrent/core'
import OrgTree, { PersonAvatar, plural } from './OrgTree'
import SubstitutionsTab from './SubstitutionsTab'
import RegionDrawer from './RegionDrawer'
import { UserPicker } from '../../tasks/UserPicker'

/**
 * Структура компании: кто за что отвечает.
 *
 * Три таба:
 *  - Оргструктура  — дерево владелец → отделы → регионы;
 *  - Ответственность — таблица «регион → согласует → почему»,
 *    буквальная визуализация резолвера маршрутизации;
 *  - Замещения — назначение/отзыв временных согласующих.
 *
 * Страница доступна в меню всем сотрудникам консоли, но данные —
 * только владельцу; менеджеру объясняем отказ, а не прячем пункт
 * (тот же паттерн, что в настройках).
 */

const TABS = [
  { key: 'tree', label: 'Оргструктура' },
  { key: 'responsibility', label: 'Ответственность' },
  { key: 'substitutions', label: 'Замещения' },
]

const VIA_LABEL = {
  primary:            'менеджер региона',
  substitution:       'замещение',
  escalated_to_owner: 'эскалация владельцу',
}

function ResponsibilityTab({ data }) {
  return (
    <div className="table-wrap">
      <table className="table">
        <thead>
          <tr>
            <th>Регион</th>
            <th>Согласует</th>
            <th>Основание</th>
            <th>Менеджер региона</th>
          </tr>
        </thead>
        <tbody>
          {data.regions.map((node) => {
            const res = node.responsible || {}
            return (
              <tr key={node.id}>
                <td>
                  <span className={`org-region__dot org-region__dot--${node.routing}`} />
                  {' '}{node.name}
                </td>
                <td>
                  {res.user ? (
                    <span className="org-cell-person">
                      <PersonAvatar name={res.user.full_name} size="xs" />
                      {res.user.full_name}
                    </span>
                  ) : '—'}
                </td>
                <td>
                  {VIA_LABEL[res.via] || '—'}
                  {res.via === 'substitution' && res.substitution && (
                    <span className="text-muted">
                      {' '}до {new Date(res.substitution.ends_at)
                        .toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}
                    </span>
                  )}
                </td>
                <td>{res.manager ? res.manager.full_name : <span className="text-muted">не назначен</span>}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function DepartmentDialog({ dep, onChanged, onClose }) {
  const isNew = !dep.id
  const [name, setName] = useState(dep.name || '')
  const [adding, setAdding] = useState(null)
  const [err, setErr] = useState('')
  const [saving, setSaving] = useState(false)

  const save = async () => {
    setErr(''); setSaving(true)
    try {
      if (isNew) await orgApi.createDepartment({ name: name.trim() })
      else await orgApi.updateDepartment(dep.id, { name: name.trim() })
      onChanged()
    } catch (e) {
      setErr(e.response?.data?.message || 'Не удалось сохранить')
      setSaving(false)
    }
  }

  const addMember = async (user) => {
    if (!user) return
    setErr('')
    try {
      await orgApi.addDepartmentMember(dep.id, user.id)
      setAdding(null)
      onChanged()
    } catch (e) {
      setErr(e.response?.data?.message || 'Не удалось добавить')
    }
  }

  const removeMember = async (user) => {
    try {
      await orgApi.removeDepartmentMember(dep.id, user.id)
      onChanged()
    } catch (e) {
      setErr(e.response?.data?.message || 'Не удалось убрать')
    }
  }

  const removeDep = async () => {
    if (!confirm(`Удалить отдел «${dep.name}»?`)) return
    try {
      await orgApi.removeDepartment(dep.id)
      onChanged()
    } catch (e) {
      setErr(e.response?.data?.message || 'Не удалось удалить')
    }
  }

  return (
    <div className="org-modal" role="dialog" aria-label="Отдел">
      <div className="org-modal__box">
        <h3 className="org-h3">{isNew ? 'Новый отдел' : dep.name}</h3>
        <div className="field">
          <label className="field__label">Название</label>
          <input className="input" value={name} maxLength={100}
            onChange={(e) => setName(e.target.value)} placeholder="HR отдел" />
        </div>

        {!isNew && (
          <div className="field">
            <label className="field__label">
              Сотрудники ({dep.members?.length || 0})
            </label>
            <div className="org-members">
              {(dep.members || []).map((m) => (
                <span key={m.id} className="org-members__item">
                  <PersonAvatar name={m.full_name} size="xs" />
                  {m.full_name}
                  <button type="button" aria-label={`Убрать: ${m.full_name}`}
                    onClick={() => removeMember(m)}>×</button>
                </span>
              ))}
            </div>
            {adding !== null ? (
              <UserPicker selected={adding} onChange={addMember}
                placeholder="Найти сотрудника..." />
            ) : (
              <button className="btn btn--sm" onClick={() => setAdding(false)}>
                + Добавить сотрудника
              </button>
            )}
          </div>
        )}

        {err && <div className="alert alert--error">{err}</div>}

        <div className="org-subform__actions">
          <button className="btn btn--primary" disabled={saving || !name.trim()}
            onClick={save}>
            {saving ? <span className="spinner" /> : 'Сохранить'}
          </button>
          {!isNew && <button className="btn" onClick={removeDep}>Удалить отдел</button>}
          <button className="btn" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    </div>
  )
}

export default function AdminStructurePage() {
  const { user } = useAuthStore()
  const isOwner = user?.role === 'admin'

  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const tab = TABS.some((t) => t.key === tabParam) ? tabParam : TABS[0].key
  const selectTab = (key) => {
    setSearchParams(key === TABS[0].key ? {} : { tab: key }, { replace: true })
  }

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [drawerRegionId, setDrawerRegionId] = useState(null)
  const [department, setDepartment] = useState(null) // отдел для модалки

  const load = () => {
    orgApi.structure()
      .then((r) => { setData(r.data?.data || null); setError('') })
      .catch(() => setError('Не удалось загрузить структуру'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (isOwner) load()
    else setLoading(false)
  }, [isOwner])

  // Drawer и модалка отдела держат только id: после мутаций показываем
  // свежие данные из перезагруженной структуры, а не снапшот на момент
  // открытия.
  const drawerRegion = data && drawerRegionId
    ? data.regions.find((r) => r.id === drawerRegionId) || null
    : null
  const openDepartment = data && department
    ? (data.departments.find((d) => d.id === department.id) || department)
    : department

  if (!isOwner) {
    return (
      <div>
        <PageHeader title="Структура компании" />
        <div className="p-page">
          <div className="alert">
            Структуру компании и замещения видит и настраивает владелец.
            Если вам нужен доступ — обратитесь к нему.
          </div>
        </div>
      </div>
    )
  }

  const stats = data && {
    drivers: data.regions.reduce((n, r) => n + (r.counts?.drivers || 0), 0),
    managers: new Set(
      data.regions.map((r) => r.responsible?.manager?.id).filter(Boolean),
    ).size,
    substitutions: data.substitutions?.length || 0,
  }

  return (
    <div className="org-page">
      <PageHeader
        title="Структура компании"
        right={stats && (
          <div className="org-stats">
            <div><b>{stats.drivers}</b><span>{plural(stats.drivers, 'Водитель', 'Водителя', 'Водителей')}</span></div>
            <div><b>{stats.managers}</b><span>{plural(stats.managers, 'Менеджер', 'Менеджера', 'Менеджеров')}</span></div>
            <div><b>{stats.substitutions}</b><span>{plural(stats.substitutions, 'Замещение', 'Замещения', 'Замещений')}</span></div>
          </div>
        )}
      />

      <div className="p-page flex-col org-page__body">
        <div className="org-tabs" role="tablist">
          {TABS.map((t) => (
            <button key={t.key} role="tab" aria-selected={tab === t.key}
              className={`org-tabs__item ${tab === t.key ? 'org-tabs__item--active' : ''}`}
              onClick={() => selectTab(t.key)}>
              {t.label}
            </button>
          ))}
          {tab === 'tree' && (
            <button className="btn btn--sm org-tabs__action"
              onClick={() => setDepartment({})}>
              + Отдел
            </button>
          )}
        </div>

        {loading && (
          <div className="org-page__loading">
            <div className="spinner" />
          </div>
        )}
        {error && !loading && <div className="alert alert--error">{error}</div>}

        {data && !loading && tab === 'tree' && (
          <OrgTree data={data}
            onOpenRegion={(node) => setDrawerRegionId(node.id)}
            onOpenDepartment={setDepartment} />
        )}
        {data && !loading && tab === 'responsibility' && (
          <ResponsibilityTab data={data} />
        )}
        {!loading && !error && tab === 'substitutions' && (
          <SubstitutionsTab regions={data?.regions || []} onChanged={load} />
        )}
      </div>

      {drawerRegion && (
        <RegionDrawer region={drawerRegion}
          regions={data.regions}
          onChanged={load}
          onClose={() => setDrawerRegionId(null)} />
      )}
      {openDepartment && (
        <DepartmentDialog dep={openDepartment}
          onChanged={() => { load(); if (!openDepartment.id) setDepartment(null) }}
          onClose={() => setDepartment(null)} />
      )}
    </div>
  )
}
